import React, { useState } from "react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import { Phone, Mail, Clock, MapPin, MessageCircle, Send } from "lucide-react";

interface FormData {
  name: string;
  subject: string;
  email: string;
  message: string;
}

interface ContactInfo {
  icon: React.ReactNode;
  title: string;
  content: string;
  description: string;
}

interface BusinessHour {
  day: string;
  hours: string;
}

const Contact: React.FC = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasSubmit, setHasSubmit] = useState<boolean>(false);

  const initials = {
    name: "",
    subject: "",
    email: "",
    message: "",
  };
  const [formData, setFormData] = useState<FormData>(initials);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);
    setHasSubmit(true);

    // Submit to backend only if all required fields are filled and valid
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
    if (
      formData.name &&
      formData.email &&
      isEmailValid &&
      formData.subject &&
      formData.message
    ) {
      fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          access_key: "9ee274fa-ccca-4b45-96de-f1d860e32a91",
          from_name: "PAVEMARK Website",
          // redirect: "http://localhost:8080", // use toast
        }),
      })
        .then(async (_response) => {
          // Note: This requires Supabase backend integration
          toast({
            title: "Formulaire reçu!",
            description:
              "Nous avons bien reçu votre message et vous contacterons bientôt.",
            className: "bg-green-600 text-white",
          });

          // Reset form
          setHasSubmit(false);
          setFormData(initials);

          setIsSubmitting(false);
        })
        .catch((_err) => {
          toast({
            title: "Erreur d'envoi",
            description: "Une ou plusieurs erreurs dans le formulaire.",
            variant: "destructive",
          });

          setIsSubmitting(false);
        });
    } else {
      toast({
        title: "Erreur",
        description: "Une ou plusieurs erreurs dans le formulaire.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const contactInfos: ContactInfo[] = [
    {
      icon: <Phone className="h-6 w-6" />,
      title: "Téléphone",
      content: atob("KDUxNCkgNzE4LTUxNDQ="),
      description: "Appelez-nous directement",
    },
    {
      icon: <Mail className="h-6 w-6" />,
      title: "E-mail",
      content: atob("aW5mby5wYXZlbWFya0BnbWFpbC5jb20="),
      description: "Écrivez-nous à tout moment",
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: "Heures d'ouverture",
      content: "Lun-Sam: 8h00 à 20h00",
      description: "Dimanche fermé",
    },
    // {
    //   icon: <MessageCircle className="h-6 w-6" />,
    //   title: "Messenger",
    //   content: "Scan QR Code",
    //   description: "Contact direct via Messenger",
    // },
  ];

  const businessHours: BusinessHour[] = [
    { day: "Lundi", hours: "8:00 à 20:00" },
    { day: "Mardi", hours: "8:00 à 20:00" },
    { day: "Mercredi", hours: "8:00 à 20:00" },
    { day: "Jeudi", hours: "8:00 à 20:00" },
    { day: "Vendredi", hours: "8:00 à 20:00" },
    { day: "Samedi", hours: "8:00 à 20:00" },
    { day: "Dimanche", hours: "Fermé" },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
          <img
            src={`${process.env.PUBLIC_URL}/photo-1544197150-b99a580bb7a8.jpg`}
            alt="Contact us"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge
            variant="outline"
            className="mb-4 text-amber-400 border-amber-400"
          >
            Contactez-nous
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Contactez nous
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-8">
            Nous aimerions avoir de vos nouvelles.
            <br /> Nous sommes là pour répondre à vos questions
          </p>
          <p className="text-lg text-amber-400 font-semibold">
            Pour toute question ou demande de soumission, nous serons ravis de
            vous aider
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {contactInfos.map((info: ContactInfo, index: number) => (
              <div key={`contact-${index}`}>
                {index === 0 && (
                  <a href={`tel:${atob("KDUxNCkgNzE4LTUxNDQ=")}`}>
                    <Card
                      key={index}
                      className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 shadow-lg text-center"
                    >
                      <CardContent className="p-8">
                        <div className="mx-auto mb-6 w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center text-amber-600 group-hover:from-amber-500 group-hover:to-orange-500 group-hover:text-white transition-all">
                          {info.icon}
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                          {info.title}
                        </h3>
                        <p className="text-lg font-medium text-gray-800 mb-2">
                          {info.content}
                        </p>
                        <p className="text-sm text-gray-600">
                          {info.description}
                        </p>
                      </CardContent>
                    </Card>
                  </a>
                )}

                {index > 0 && (
                  <Card
                    key={index}
                    className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 shadow-lg text-center"
                  >
                    <CardContent className="p-8">
                      <div className="mx-auto mb-6 w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center text-amber-600 group-hover:from-amber-500 group-hover:to-orange-500 group-hover:text-white transition-all">
                        {info.icon}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                        {info.title}
                      </h3>
                      <p className="text-lg font-medium text-gray-800 mb-2">
                        {info.content}
                      </p>
                      <p className="text-sm text-gray-600">
                        {info.description}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Envoyez-nous un message
                </h2>
                <p className="text-lg text-gray-600">
                  Remplissez le formulaire ci-dessous et nous vous contacterons
                  rapidement pour discuter de votre projet.
                </p>
              </div>

              <Card className="shadow-xl border-0">
                <CardHeader>
                  <CardTitle className="text-2xl">
                    Formulaire de contact
                  </CardTitle>
                  <CardDescription>
                    Tous les champs marqués d'un astérisque (*) sont
                    obligatoires
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form
                    onSubmit={handleSubmit}
                    className="space-y-6"
                    noValidate
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nom *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          className="transition-all focus:ring-2 focus:ring-amber-500"
                        />
                        {formData.name === "" && hasSubmit && (
                          <div className="text-red-600 text-sm mt-1">
                            Veuillez entrer votre nom complet.
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject">Subject</Label>
                        <Input
                          id="subject"
                          name="subject"
                          type="text"
                          value={formData.subject}
                          onChange={handleInputChange}
                          className="transition-all focus:ring-2 focus:ring-amber-500"
                        />
                        {formData.subject === "" && hasSubmit && (
                          <div className="text-red-600 text-sm mt-1">
                            Veuillez préciser le sujet.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="transition-all focus:ring-2 focus:ring-amber-500"
                      />
                      {formData.email === "" && hasSubmit ? (
                        <div className="text-red-600 text-sm mt-1">
                          Veuillez entrer une adresse email valide.
                        </div>
                      ) : (
                        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
                        hasSubmit && (
                          <div className="text-red-600 text-sm mt-1">
                            Format d'email invalide.
                          </div>
                        )
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        value={formData.message}
                        onChange={handleInputChange}
                        className="transition-all focus:ring-2 focus:ring-amber-500"
                        placeholder="Décrivez votre projet ou votre demande..."
                      />
                      {formData.message === "" && hasSubmit && (
                        <div className="text-red-600 text-sm mt-1">
                          Veuillez écrire votre message.
                        </div>
                      )}
                    </div>

                    <div className="text-sm text-gray-600">
                      En soumettant ce formulaire, vous acceptez que nous
                      traitions vos données conformément à notre Politique de
                      confidentialité.
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-lg py-6 shadow-lg hover:shadow-xl transition-all"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Envoi en cours...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Send className="h-5 w-5" />
                          Envoyer un message
                        </div>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Business Hours & Additional Info */}
            <div className="space-y-8">
              {/* Business Hours */}
              <Card className="shadow-xl border-0">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-3">
                    <Clock className="h-6 w-6 text-amber-600" />
                    Heures d'ouverture
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {businessHours.map(
                      (schedule: BusinessHour, index: number) => (
                        <div
                          key={index}
                          className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0"
                        >
                          <span className="font-medium text-gray-900">
                            {schedule.day}
                          </span>
                          <span
                            className={`font-semibold ${
                              schedule.hours === "Fermé"
                                ? "text-red-600"
                                : "text-green-600"
                            }.jpg`}
                          >
                            {schedule.hours}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* QR Code Section 
              <Card className="shadow-xl border-0 bg-gradient-to-br from-amber-50 to-orange-50">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-3">
                    <MessageCircle className="h-6 w-6 text-amber-600" />
                    Contact rapide
                  </CardTitle>
                  <CardDescription className="text-base">
                    Scannez le code QR ci-bas avec votre téléphone pour nous
                    contacter directement sur messenger
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="bg-white p-6 rounded-xl inline-block shadow-lg">
                    <div className="w-40 h-40 bg-gray-200 rounded-lg flex items-center justify-center">
                      <MessageCircle className="h-16 w-16 text-gray-400" />
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-gray-600">
                    Code QR pour Messenger
                  </p>
                </CardContent>
              </Card>*/}

              {/* Call to Action */}
              <Card className="shadow-xl border-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold mb-4">
                    Besoin d'une réponse rapide ?
                  </h3>
                  <p className="text-amber-100 mb-6">
                    Appelez-nous directement pour une consultation immédiate
                  </p>
                  <a href={`tel:${atob("KDUxNCkgNzE4LTUxNDQ=")}`}>
                    <Button
                      variant="outline"
                      size="lg"
                      className="bg-white text-amber-600 border-white hover:bg-amber-50"
                    >
                      <Phone className="h-5 w-5 mr-2" />
                      {atob("KDUxNCkgNzE4LTUxNDQ=")}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
